import { survivorEquipmentBuildStrategy } from '@/data/survivorEquipmentBuildStrategies'
import { getSurvivorPerkRoleIds } from '@/data/survivorPerkRoles'

const BUILT_TO_LAST_MULTIPLIER = 2.98
const FLASHLIGHT_DISAPPEARANCE_PERKS = new Set([
  'dance_with_me',
  'deception',
  'lightweight',
  'parental_guidance',
  'quick_and_quiet'
])
const FOG_PRERUN_DISAPPEARANCE_PERKS = new Set([
  'dance_with_me',
  'deception',
  'lightweight',
  'lucky_break',
  'parental_guidance',
  'poised',
  'quick_and_quiet'
])
const KEY_CHEST_ROUTE_PERKS = new Set([
  'ace_in_the_hole',
  'appraisal',
  'moment_of_glory',
  'pharmacy',
  'plunderers_instinct',
  'specialist'
])
const KEY_HATCH_ROUTE_PERKS = new Set(['left_behind', 'low_profile', 'sole_survivor'])
const KEY_TEAM_LINK_PERKS = new Set([
  'blood_pact',
  'teamwork_collective_stealth',
  'teamwork_full_circuit',
  'teamwork_power_of_two',
  'teamwork_soft_spoken',
  'teamwork_throw_down',
  'teamwork_toughen_up'
])
const KEY_TEAM_PAYOFF_PERKS = new Set([
  'better_than_new',
  'corrective_action',
  'friendly_competition',
  'leader',
  'prove_thyself',
  'streetwise',
  'vigil'
])
const STRATEGY_CENTRAL_PERKS = {
  built_to_last_capacity_cycle: ['built_to_last'],
  scavenger_capacity_cycle: ['scavenger'],
  residual_flashbang_pipeline: ['residual_manifest', 'flashbang'],
  brand_new_part_opener: ['specialist'],
  flashlight_chase_control: ['champion_of_light', 'residual_manifest'],
  flashlight_pickup_rescue: ['background_player'],
  flashlight_long_blind: ['champion_of_light'],
  flashlight_endurance_loop: ['built_to_last'],
  key_team_locator: Array.from(KEY_TEAM_LINK_PERKS),
  key_killer_read: ['eyes_of_belmont'],
  key_chest_route: Array.from(KEY_CHEST_ROUTE_PERKS),
  key_hatch_escape: Array.from(KEY_HATCH_ROUTE_PERKS),
  key_obsession_link: ['blood_pact'],
  fog_prerun_disappearance: Array.from(FOG_PRERUN_DISAPPEARANCE_PERKS),
  anti_slug_medical_reset: ['were_gonna_live_forever', 'buckle_up', 'for_the_people']
}

function clamp (value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value))
}

function round (value, precision) {
  const factor = Math.pow(10, precision || 0)
  return Math.round(value * factor) / factor
}

function metricValue (result, id) {
  const metric = (result.metrics || []).find(entry => entry.id === id)
  return metric ? Number(metric.value) || 0 : 0
}

function strategyResult (id, adjustment, metrics, evidence) {
  return {
    id,
    definition: survivorEquipmentBuildStrategy(id),
    adjustment,
    metrics: metrics || [],
    evidence: evidence || []
  }
}

function hasPair (addonIds, first, second) {
  return addonIds.indexOf(first) !== -1 && addonIds.indexOf(second) !== -1
}

export function evaluateEquipmentBuildStrategy (equipmentPackage, result, perks, plan) {
  const perkIds = new Set((perks || []).map(perk => perk.id))
  const centralPerkId = perks && perks[0] ? perks[0].id : ''
  const addonIds = equipmentPackage.addonIds || []
  const scenarioId = result.scenario.id
  const category = equipmentPackage.category
  const primaryPlan = plan && (plan.primary || plan.id)
  const pickupDenialCount = (perks || []).filter(perk => (
    getSurvivorPerkRoleIds(perk.id).indexOf('pickup_denial') !== -1
  )).length
  const rechargeableCapacityLoop = perkIds.has('built_to_last') || perkIds.has('scavenger')
  const fogPrerunPerkCount = Array.from(perkIds).filter(perkId => FOG_PRERUN_DISAPPEARANCE_PERKS.has(perkId)).length
  const keyChestPerkCount = Array.from(perkIds).filter(perkId => KEY_CHEST_ROUTE_PERKS.has(perkId)).length
  const keyHatchPerkCount = Array.from(perkIds).filter(perkId => KEY_HATCH_ROUTE_PERKS.has(perkId)).length
  const keyTeamLinkCount = Array.from(perkIds).filter(perkId => KEY_TEAM_LINK_PERKS.has(perkId)).length
  const keyTeamPayoffCount = Array.from(perkIds).filter(perkId => KEY_TEAM_PAYOFF_PERKS.has(perkId)).length
  const keyTeamRoute = keyTeamLinkCount >= 1 && keyTeamLinkCount + keyTeamPayoffCount >= 2
  const keyObsessionRoute = perkIds.has('blood_pact') && keyTeamLinkCount + keyTeamPayoffCount >= 2
  const candidates = []
  let penalty = 0

  if (category === 7 && scenarioId === 'generator_capacity' && perkIds.has('built_to_last')) {
    const charges = metricValue(result, 'charges')
    const capacityBonus = Math.max(0, charges - 32)
    const hasBnp = addonIds.indexOf('brand_new_part') !== -1
    candidates.push(strategyResult(
      'built_to_last_capacity_cycle',
      18 + Math.min(10, capacityBonus * 0.5) - (hasBnp ? 24 : 0),
      [
        { id: 'effectiveCharges', value: round(charges * BUILT_TO_LAST_MULTIPLIER, 2) },
        { id: 'lifetimeGeneratorProgress', value: round(charges * BUILT_TO_LAST_MULTIPLIER / 90 * 100, 1), suffix: '%' },
        { id: 'builtToLastMultiplier', value: BUILT_TO_LAST_MULTIPLIER }
      ],
      [{ id: 'exactPerkMechanic', value: hasBnp ? -24 : 18, detail: 'built_to_last' }]
    ))
  }

  if (category === 7 && scenarioId === 'generator_capacity' && perkIds.has('scavenger')) {
    const charges = metricValue(result, 'charges')
    const hasBnp = addonIds.indexOf('brand_new_part') !== -1
    candidates.push(strategyResult(
      'scavenger_capacity_cycle',
      17 + Math.min(8, Math.max(0, charges - 32) * 0.4) - (hasBnp ? 20 : 0),
      [
        { id: 'rechargeChargesPerCycle', value: charges },
        { id: 'projectedTwoCycleCharges', value: charges * 2 }
      ],
      [{ id: 'exactPerkMechanic', value: hasBnp ? -20 : 17, detail: 'scavenger' }]
    ))
  }

  const residualPipeline = category === 7 &&
    (scenarioId === 'generator_capacity' || scenarioId === 'generator_burst') &&
    perkIds.has('residual_manifest') &&
    perkIds.has('flashbang')
  if (residualPipeline) {
    const generatorProgress = metricValue(result, 'generatorProgress')
    const objectiveProgress = metricValue(result, 'generatorObjectiveProgress')
    const reachesFlashbang = generatorProgress >= 40
    const capacityPair = hasPair(addonIds, 'wire_spool', 'scraps')
    const bnpCapacityPair = hasPair(addonIds, 'brand_new_part', 'wire_spool')
    const routePair = rechargeableCapacityLoop ? capacityPair : bnpCapacityPair
    candidates.push(strategyResult(
      'residual_flashbang_pipeline',
      (reachesFlashbang ? 20 : 4) +
        (equipmentPackage.item.id === 'commodious_toolbox' ? 3 : 0) +
        (routePair ? 14 : 0) -
        (!rechargeableCapacityLoop && capacityPair ? 10 : 0) +
        (perkIds.has('champion_of_light') ? 5 : 0),
      [
        { id: 'flashbangProgressThreshold', value: 40, suffix: '%' },
        { id: 'toolboxGeneratorProgress', value: generatorProgress, suffix: '%' },
        { id: 'toolboxObjectiveProgress', value: objectiveProgress, suffix: '%' },
        { id: 'permanentRequirementReduction', value: bnpCapacityPair ? 10 : 0 },
        { id: 'rechargeableCapacityRoute', value: rechargeableCapacityLoop ? 1 : 0 },
        { id: 'guaranteedChestFlashlight', value: 1 }
      ],
      [
        { id: 'exactPerkMechanic', value: reachesFlashbang ? 20 : 4, detail: 'flashbang' },
        { id: 'exactPerkMechanic', value: 8, detail: 'residual_manifest' }
      ]
    ))
  }

  const directGeneratorPressure = category === 7 &&
    primaryPlan === 'generator_pressure' &&
    !rechargeableCapacityLoop &&
    !residualPipeline
  if (directGeneratorPressure && (
    scenarioId !== 'brand_new_part_burst' || addonIds.indexOf('brand_new_part') === -1
  )) {
    penalty -= 24
  }

  const bnpOpener = category === 7 &&
    scenarioId === 'brand_new_part_burst' &&
    addonIds.indexOf('brand_new_part') !== -1 &&
    primaryPlan === 'generator_pressure' &&
    !perkIds.has('built_to_last') &&
    !perkIds.has('scavenger')
  if (bnpOpener) {
    const specialistBonus = perkIds.has('specialist') ? 4 : 0
    candidates.push(strategyResult(
      'brand_new_part_opener',
      13 + (equipmentPackage.item.id === 'commodious_toolbox' ? 3 : 0) + specialistBonus,
      [
        { id: 'permanentRequirementReduction', value: 10 },
        { id: 'frontLoadedObjectiveValue', value: round(metricValue(result, 'charges') + 10, 1) }
      ],
      [{ id: 'exactAddonMechanic', value: 13 + specialistBonus, detail: 'brand_new_part' }]
    ))
  }

  const chaseFlashlight = category === 3 &&
    scenarioId === 'chase_blind' &&
    (perkIds.has('champion_of_light') || centralPerkId === 'residual_manifest')
  if (chaseFlashlight) {
    const reachBatteryPair = hasPair(addonIds, 'high_end_sapphire_lens', 'long_life_battery')
    const reachCapacityPair = hasPair(addonIds, 'high_end_sapphire_lens', 'heavy_duty_battery')
    const reachPayoffPair = hasPair(addonIds, 'high_end_sapphire_lens', 'intense_halogen')
    const hasSapphire = addonIds.indexOf('high_end_sapphire_lens') !== -1
    candidates.push(strategyResult(
      'flashlight_chase_control',
      (equipmentPackage.item.id === 'utility_flashlight' ? 6 : 0) +
        (reachBatteryPair ? 18 : reachCapacityPair ? 14 : reachPayoffPair ? 12 : hasSapphire ? 5 : 0) +
        (centralPerkId === 'champion_of_light' ? 5 : 0),
      [
        { id: 'chaseBlindReach', value: metricValue(result, 'beamRangeMetres'), suffix: 'm' },
        { id: 'chaseBlindWindow', value: metricValue(result, 'blindSeconds'), suffix: 's' },
        { id: 'chaseBlindAttempts', value: metricValue(result, 'batterySeconds'), suffix: 's' }
      ],
      [{ id: 'exactPerkMechanic', value: 12, detail: 'chase_blind_reposition' }]
    ))
  }

  const pickupFlashlight = category === 3 &&
    scenarioId === 'flashlight_save' &&
    (perkIds.has('background_player') || pickupDenialCount >= 2) &&
    (centralPerkId === 'background_player' ||
      primaryPlan === 'rescue_teamplay' ||
      primaryPlan === 'linked_teamwork' ||
      pickupDenialCount >= 2)
  if (pickupFlashlight) {
    const rescuePair = hasPair(addonIds, 'wide_lens', 'long_life_battery')
    const stablePair = hasPair(addonIds, 'wide_lens', 'rubber_grip')
    const efficientPair = hasPair(addonIds, 'wide_lens', 'low_amp_filament')
    candidates.push(strategyResult(
      'flashlight_pickup_rescue',
      (equipmentPackage.item.id === 'sport_flashlight' ? 6 : 0) +
        (rescuePair ? 18 : stablePair ? 14 : efficientPair ? 12 : 0) +
        (centralPerkId === 'background_player' ? 5 : 0),
      [
        { id: 'pickupWindowMobilitySeconds', value: 5 },
        { id: 'pickupWindowHaste', value: 50, suffix: '%' }
      ],
      [{ id: 'exactPerkMechanic', value: 15, detail: perkIds.has('background_player') ? 'background_player' : 'pickup_denial' }]
    ))
  }

  const disappearancePayoff = Array.from(perkIds).some(perkId => FLASHLIGHT_DISAPPEARANCE_PERKS.has(perkId))
  const longBlind = category === 3 &&
    scenarioId === 'blind_duration_pressure' &&
    perkIds.has('champion_of_light') &&
    disappearancePayoff
  if (longBlind) {
    const durationPair = hasPair(addonIds, 'odd_bulb', 'intense_halogen') ||
      hasPair(addonIds, 'odd_bulb', 'high_end_sapphire_lens')
    candidates.push(strategyResult(
      'flashlight_long_blind',
      (equipmentPackage.item.id === 'utility_flashlight' ? 6 : 0) +
        (durationPair ? 17 : 0),
      [{ id: 'blindDurationBonus', value: metricValue(result, 'blindDurationBonus'), suffix: '%' }],
      [{ id: 'exactPerkMechanic', value: 10, detail: 'disappearance_after_blind' }]
    ))
  }
  if (category === 3 && scenarioId === 'blind_duration_pressure' && !longBlind) penalty -= 24

  const enduranceLoop = category === 3 &&
    scenarioId === 'flashlight_endurance' &&
    perkIds.has('built_to_last')
  if (enduranceLoop) {
    const economyPair = hasPair(addonIds, 'long_life_battery', 'low_amp_filament')
    const battery = metricValue(result, 'batterySeconds')
    candidates.push(strategyResult(
      'flashlight_endurance_loop',
      (economyPair ? 17 : 0) + Math.min(8, Math.max(0, battery - 12) * 0.55),
      [
        { id: 'effectiveBeamSeconds', value: round(battery * BUILT_TO_LAST_MULTIPLIER, 1) },
        { id: 'builtToLastMultiplier', value: BUILT_TO_LAST_MULTIPLIER }
      ],
      [{ id: 'exactPerkMechanic', value: 17, detail: 'built_to_last' }]
    ))
  }

  if (category === 1 && scenarioId === 'team_aura_tracking' && KEY_TEAM_LINK_PERKS.has(centralPerkId) && keyTeamRoute && !keyObsessionRoute) {
    const exactPair = hasPair(addonIds, 'braided_bauble', 'friendship_charm')
    candidates.push(strategyResult(
      'key_team_locator',
      (equipmentPackage.item.id === 'skeleton_key' ? 5 : 0) +
        (exactPair ? 20 : 0) +
        Math.min(10, (keyTeamLinkCount + keyTeamPayoffCount) * 3),
      [
        { id: 'linkedPerkCoverage', value: keyTeamLinkCount + keyTeamPayoffCount },
        { id: 'survivorReadSeconds', value: metricValue(result, 'auraSeconds'), suffix: 's' },
        { id: 'survivorReadRange', value: metricValue(result, 'auraRangeMetres'), suffix: 'm' }
      ],
      [{ id: 'exactPerkMechanic', value: 14, detail: 'locate_then_activate_proximity_payoff' }]
    ))
  }

  if (category === 1 && scenarioId === 'killer_aura_tracking' && centralPerkId === 'eyes_of_belmont') {
    const exactPair = hasPair(addonIds, 'blood_amber', 'braided_bauble')
    candidates.push(strategyResult(
      'key_killer_read',
      (equipmentPackage.item.id === 'skeleton_key' ? 5 : 0) +
        (exactPair ? 23 : addonIds.indexOf('blood_amber') !== -1 ? 7 : 0),
      [
        { id: 'globalKillerRead', value: metricValue(result, 'globalKillerAura') },
        { id: 'eyesOfBelmontReadSeconds', value: metricValue(result, 'auraSeconds') + 2, suffix: 's' },
        { id: 'reciprocalAuraExposure', value: addonIds.indexOf('blood_amber') !== -1 ? 1 : 0 }
      ],
      [{ id: 'exactPerkMechanic', value: 18, detail: 'eyes_of_belmont_extends_blood_amber' }]
    ))
  }

  if (category === 1 && scenarioId === 'chest_endgame_utility' && KEY_CHEST_ROUTE_PERKS.has(centralPerkId) && keyChestPerkCount >= 2) {
    const exactPair = hasPair(addonIds, 'friendship_charm', 'shrill_whistle')
    candidates.push(strategyResult(
      'key_chest_route',
      (equipmentPackage.item.id === 'skeleton_key' ? 5 : 0) +
        (exactPair ? 21 : 0) +
        Math.min(10, keyChestPerkCount * 4),
      [
        { id: 'chestPerkCoverage', value: keyChestPerkCount },
        { id: 'lockedChestSeconds', value: metricValue(result, 'interactionSeconds'), suffix: 's' },
        { id: 'keyUses', value: metricValue(result, 'uses') }
      ],
      [{ id: 'exactPerkMechanic', value: 16, detail: 'locked_chest_rare_item_conversion' }]
    ))
  }

  if (category === 1 && scenarioId === 'chest_endgame_utility' && KEY_HATCH_ROUTE_PERKS.has(centralPerkId) && keyHatchPerkCount >= 2) {
    const exactPair = hasPair(addonIds, 'friendship_charm', 'shrill_whistle')
    candidates.push(strategyResult(
      'key_hatch_escape',
      (equipmentPackage.item.id === 'skeleton_key' || equipmentPackage.item.id === 'dull_key' ? 5 : 0) +
        (exactPair ? 19 : 0) +
        Math.min(9, keyHatchPerkCount * 4),
      [
        { id: 'hatchFallbackPerks', value: keyHatchPerkCount },
        { id: 'closedHatchReopen', value: metricValue(result, 'hatchUnlocked') },
        { id: 'hatchChannelSeconds', value: metricValue(result, 'interactionSeconds'), suffix: 's' }
      ],
      [{ id: 'exactPerkMechanic', value: 16, detail: 'closed_hatch_last_survivor_fallback' }]
    ))
  }

  if (category === 1 && scenarioId === 'obsession_coordination' && centralPerkId === 'blood_pact' && keyObsessionRoute) {
    const exactPair = hasPair(addonIds, 'friendship_charm', 'unique_wedding_ring')
    candidates.push(strategyResult(
      'key_obsession_link',
      (equipmentPackage.item.id === 'skeleton_key' ? 5 : 0) +
        (exactPair ? 22 : 0) +
        Math.min(8, (keyTeamLinkCount + keyTeamPayoffCount) * 3),
      [
        { id: 'permanentObsessionLink', value: metricValue(result, 'permanentObsessionAura') },
        { id: 'linkedPerkCoverage', value: keyTeamLinkCount + keyTeamPayoffCount }
      ],
      [{ id: 'exactPerkMechanic', value: 18, detail: 'blood_pact_obsession_link' }]
    ))
  }
  const fogPrerun = category === 8 &&
    scenarioId === 'large_lasting_cloud' &&
    fogPrerunPerkCount >= 2
  if (fogPrerun) {
    const wideLongPair = hasPair(addonIds, 'mushroom_formula', 'oily_sap')
    candidates.push(strategyResult(
      'fog_prerun_disappearance',
      (equipmentPackage.item.id === 'vigo_s_fog_vial' ? 4 : 0) +
        (wideLongPair ? 18 : 0) +
        Math.min(12, fogPrerunPerkCount * 4),
      [
        { id: 'preRunDisappearancePerks', value: fogPrerunPerkCount },
        { id: 'outsideInRadius', value: metricValue(result, 'radiusMetres'), suffix: 'm' },
        { id: 'outsideInDuration', value: metricValue(result, 'durationSeconds'), suffix: 's' }
      ],
      [{ id: 'exactPerkMechanic', value: 14 + fogPrerunPerkCount * 3, detail: 'line_of_sight_plus_trace_denial' }]
    ))
  }
  if (category === 8 && !fogPrerun) penalty -= 20

  const antiSlugPerks = ['were_gonna_live_forever', 'buckle_up', 'for_the_people']
    .filter(perkId => perkIds.has(perkId)).length
  if (category === 6 && scenarioId.indexOf('altruistic_heal') === 0 && antiSlugPerks >= 2) {
    candidates.push(strategyResult(
      'anti_slug_medical_reset',
      12 + antiSlugPerks * 4,
      [{ id: 'antiSlugPerkCoverage', value: antiSlugPerks }],
      [{ id: 'exactPerkMechanic', value: 12 + antiSlugPerks * 4, detail: 'dying_state_heal' }]
    ))
  }

  const flashlightPerkCount = ['champion_of_light', 'residual_manifest', 'background_player']
    .filter(perkId => perkIds.has(perkId)).length
  const explicitFlashlightIntent = flashlightPerkCount > 0 || pickupDenialCount >= 2 ||
    (primaryPlan === 'item_economy' && perkIds.has('built_to_last'))
  if (category === 3 && !explicitFlashlightIntent) penalty -= 30
  if (flashlightPerkCount >= 2 && category !== 3 && !residualPipeline) penalty -= 18
  if (antiSlugPerks >= 2 && category !== 6) penalty -= 14
  if ((perkIds.has('built_to_last') || perkIds.has('scavenger')) && addonIds.indexOf('brand_new_part') !== -1) penalty -= 12

  candidates.forEach(candidate => {
    const centralPerks = STRATEGY_CENTRAL_PERKS[candidate.id] || []
    candidate.centralPerkFit = centralPerks.indexOf(centralPerkId) !== -1
    candidate.centralPerkId = candidate.centralPerkFit ? centralPerkId : ''
    if (candidate.centralPerkFit) candidate.adjustment += 6
  })

  const ordered = candidates.sort((first, second) => second.adjustment - first.adjustment)
  ordered.forEach(candidate => {
    const coverageByStrategy = {
      built_to_last_capacity_cycle: perkIds.has('built_to_last') ? 1 : 0,
      scavenger_capacity_cycle: perkIds.has('scavenger') ? 1 : 0,
      residual_flashbang_pipeline: 2 +
        (perkIds.has('champion_of_light') ? 1 : 0) +
        (perkIds.has('background_player') ? 1 : 0),
      brand_new_part_opener: perkIds.has('specialist') ? 1 : 0,
      flashlight_chase_control: perkIds.has('champion_of_light') ? 1 : 0,
      flashlight_pickup_rescue: Math.max(perkIds.has('background_player') ? 1 : 0, pickupDenialCount),
      flashlight_long_blind: 1 + (disappearancePayoff ? 1 : 0),
      flashlight_endurance_loop: perkIds.has('built_to_last') ? 1 : 0,
      key_team_locator: keyTeamLinkCount + keyTeamPayoffCount,
      key_killer_read: perkIds.has('eyes_of_belmont') ? 1 : 0,
      key_chest_route: keyChestPerkCount,
      key_hatch_escape: keyHatchPerkCount,
      key_obsession_link: keyTeamLinkCount + keyTeamPayoffCount,
      fog_prerun_disappearance: fogPrerunPerkCount,
      anti_slug_medical_reset: antiSlugPerks
    }
    candidate.perkCoverage = coverageByStrategy[candidate.id] || 0
  })
  const primary = ordered[0] || null
  const supportingAdjustment = ordered.slice(1).reduce((sum, candidate) => (
    sum + Math.max(0, candidate.adjustment) * 0.25
  ), 0)
  const residualBnpRoute = primary && primary.id === 'residual_flashbang_pipeline' &&
    !rechargeableCapacityLoop && hasPair(addonIds, 'brand_new_part', 'wire_spool')
  const adjustment = clamp(
    penalty + (primary ? primary.adjustment : 0) + supportingAdjustment,
    -30,
    residualBnpRoute ? 44 : 32
  )

  return {
    primary,
    candidates: ordered,
    penalty,
    adjustment: round(adjustment, 3),
    metrics: ordered.reduce((all, candidate) => all.concat(candidate.metrics), []),
    evidence: ordered.reduce((all, candidate) => all.concat(candidate.evidence), [])
  }
}
